import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Ingredient, IngredientPrice } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

/**
 * Ingredient Prices Page - Manage ingredient pricing (SCD2)
 */
const IngredientPrices = () => {
  savePathToLocalStorage();

  const getIngredientId = (ing: Ingredient) => ing.ID || 0;

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [prices, setPrices] = useState<IngredientPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  // Guards the create button so a slow request can't be submitted twice.
  const [saving, setSaving] = useState(false);

  // Price form state
  const [newPrice, setNewPrice] = useState({
    ingredient_id: 0,
    price_per_kg: "",
    effective_from: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    loadIngredients();
  }, []);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getIngredients();
      setIngredients(data);
    } catch (err: any) {
      toast.error("Failed to load ingredients: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPricesForIngredient = async (ingredientId: number) => {
    try {
      const data = await mesApi.getIngredientPrices(ingredientId);
      setPrices(data);
    } catch (err: any) {
      toast.error("Failed to load prices: " + err.message);
    }
  };

  const handleSelectIngredient = (ingredient: Ingredient) => {
    setSelectedIngredient(ingredient);
    loadPricesForIngredient(getIngredientId(ingredient));
  };

  const handleCreatePrice = async () => {
    if (saving) return;
    if (!newPrice.ingredient_id || !newPrice.price_per_kg) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await mesApi.createIngredientPrice({
        Ingredient: newPrice.ingredient_id,
        PricePerKg: parseFloat(newPrice.price_per_kg),
        ValidFrom: newPrice.effective_from,
      });
      toast.success("Price added successfully");
      setShowPriceModal(false);
      setNewPrice({
        ingredient_id: selectedIngredient ? getIngredientId(selectedIngredient) : 0,
        price_per_kg: "",
        effective_from: new Date().toISOString().split("T")[0],
      });
      if (selectedIngredient) {
        loadPricesForIngredient(getIngredientId(selectedIngredient));
      }
    } catch (err: any) {
      toast.error("Failed to add price: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("hr-HR");
  };

  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EUR/kg";
  };

  const getCurrentPrice = (ingredient: Ingredient): IngredientPrice | undefined => {
    const now = new Date();
    return ingredient.IngredientPrice?.find((p) => {
      const from = new Date(p.ValidFrom || "");
      const to = p.ValidTo ? new Date(p.ValidTo) : null;
      return from <= now && (!to || to >= now);
    });
  };

  return (
    <div className="mes-page ingredient-prices-page">
      <Header title="MES - Cijene sastojaka" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "1rem" }}>
            {/* Ingredients List */}
            <div className="mes-section">
              <h3>Sastojci</h3>
              {loading ? (
                <div className="loading-state">Loading...</div>
              ) : (
                <div style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto" }}>
                  {ingredients.map((ing) => {
                    const currentPrice = getCurrentPrice(ing);
                    const ingId = getIngredientId(ing);
                    const selectedId = selectedIngredient ? getIngredientId(selectedIngredient) : null;
                    return (
                      <div
                        key={ingId}
                        onClick={() => handleSelectIngredient(ing)}
                        style={{
                          padding: "0.75rem",
                          marginBottom: "0.5rem",
                          background: selectedId === ingId ? "rgba(75, 153, 135, 0.2)" : "rgba(255, 255, 255, 0.02)",
                          borderRadius: "4px",
                          cursor: "pointer",
                          border: selectedId === ingId ? "1px solid #4b9987" : "1px solid transparent",
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{ing.IngredientName}</div>
                        <div style={{ fontSize: "0.85rem", color: "#adb3bd" }}>
                          {currentPrice ? formatCurrency(currentPrice.PricePerKg) : "Nema cijene"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Price History */}
            <div className="mes-section prices-section">
              {selectedIngredient ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <h3 style={{ margin: 0, border: "none", padding: 0 }}>
                      Povijest cijena: {selectedIngredient.IngredientName}
                    </h3>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        setNewPrice({
                          ingredient_id: getIngredientId(selectedIngredient),
                          price_per_kg: "",
                          effective_from: new Date().toISOString().split("T")[0],
                        });
                        setShowPriceModal(true);
                      }}
                    >
                      + Nova cijena
                    </button>
                  </div>

                  {prices.length === 0 ? (
                    <div className="empty-state">
                      <p>Nema povijesti cijena za ovaj sastojak.</p>
                    </div>
                  ) : (
                    <div className="price-history">
                      {prices.map((price, index) => {
                        const isActive = !price.ValidTo || new Date(price.ValidTo) >= new Date();
                        return (
                          <div
                            key={price.ID}
                            className="price-item"
                            style={{
                              borderLeft: isActive ? "3px solid #4b9987" : "3px solid transparent",
                            }}
                          >
                            <div>
                              <span className="price-date">
                                {formatDate(price.ValidFrom)}
                                {price.ValidTo ? ` - ${formatDate(price.ValidTo)}` : " - danas"}
                              </span>
                              {isActive && (
                                <span className="status-pill status-active" style={{ marginLeft: "0.5rem" }}>
                                  <span className="status-dot"></span>
                                  AKTIVNO
                                </span>
                              )}
                            </div>
                            <span className="price-value">{formatCurrency(price.PricePerKg)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Price Chart Placeholder */}
                  <div style={{ marginTop: "1.5rem", padding: "2rem", background: "rgba(0,0,0,0.1)", borderRadius: "8px", textAlign: "center" }}>
                    <p style={{ color: "#adb3bd", margin: 0 }}>Graf kretanja cijena (TODO)</p>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>Odaberite sastojak za prikaz povijesti cijena.</p>
                </div>
              )}
            </div>
          </div>

          {/* Create Price Modal */}
          {showPriceModal && (
            <div className="mes-modal-overlay" onClick={() => setShowPriceModal(false)}>
              <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="modal-title-group">
                    <div className="modal-icon">💰</div>
                    <div className="modal-title-text">
                      <h3>NOVA CIJENA</h3>
                      <p className="modal-subtitle">Unos cijene za {selectedIngredient?.IngredientName}</p>
                    </div>
                  </div>
                  <button className="close-btn" onClick={() => setShowPriceModal(false)}>
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Sastojak</label>
                    <input
                      type="text"
                      value={selectedIngredient?.IngredientName || ""}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Cijena (EUR/kg) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newPrice.price_per_kg}
                      onChange={(e) =>
                        setNewPrice({ ...newPrice, price_per_kg: e.target.value })
                      }
                      placeholder="npr. 1.50"
                    />
                  </div>
                  <div className="form-group">
                    <label>Vrijedi od *</label>
                    <input
                      type="date"
                      value={newPrice.effective_from}
                      onChange={(e) =>
                        setNewPrice({ ...newPrice, effective_from: e.target.value })
                      }
                    />
                  </div>
                  <p className="modal-hint">
                    * Stara cijena ce automatski isteci danom prije novog datuma.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setShowPriceModal(false)} disabled={saving}>
                    Odustani
                  </button>
                  <button className="btn-submit" onClick={handleCreatePrice} disabled={saving}>
                    {saving ? "Spremanje..." : "Spremi cijenu"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default IngredientPrices;
